import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  TextField,
  Button,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SendIcon from "@mui/icons-material/Send";
import {
  getTemplates,
  sendTemplateMessage,
  getSubmissionStatus,
} from "../api/axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import api from "../api/axios";

export default function SendMessage({ type }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [preview, setPreview] = useState("");
  const [header, setHeader] = useState({ format: "NONE", text: "" });
  const [footer, setFooter] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [submitMode, setSubmitMode] = useState("Immediate");
  const [scheduledDateTime, setScheduledDateTime] = useState(
    dayjs().toISOString()
  );
  const [mode, setMode] = useState("text");
  const [textRecipients, setTextRecipients] = useState("");
  const [csvRecipients, setCsvRecipients] = useState([]);
  const [fileName, setFileName] = useState("");
  const [sending, setSending] = useState(false);
  const [submitId, setSubmitId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const pollRef = useRef(null);

  // Load templates
  useEffect(() => {
    getTemplates(type)
      .then((res) => {
        setTemplates(res.data);
        console.log("✅ Templates loaded:", res.data);
      })
      .catch((err) => {
        console.error("❌ Failed to load templates:", err);
        toast.error("Failed to load templates");
      });
  }, [type]);

  // Handle template selection
  useEffect(() => {
    const tpl = templates.find((t) => t.id === selectedTpl);
    if (!tpl) {
      setPreview("");
      setHeader({ format: "NONE", text: "" });
      setFooter("");
      return;
    }

    console.log("✅ Selected Template:", tpl);

    let components = [];
    if (tpl.metadata) {
      try {
        let parsedMetadata;
        if (typeof tpl.metadata === "string") {
          parsedMetadata = JSON.parse(tpl.metadata);
        } else {
          parsedMetadata = tpl.metadata;
        }
        components = parsedMetadata.components || [];
        console.log("✅ Parsed components from metadata:", components);
      } catch (e) {
        console.error("❌ Failed to parse metadata JSON", e);
      }
    }

    let headerFormat = "NONE";
    let headerText = "";
    let bodyText = "";
    let footerText = "";

    if (components && Array.isArray(components)) {
      const headerComponent = components.find(
        (c) => c.type?.toUpperCase() === "HEADER"
      );
      const bodyComponent = components.find(
        (c) => c.type?.toUpperCase() === "BODY"
      );
      const footerComponent = components.find(
        (c) => c.type?.toUpperCase() === "FOOTER"
      );

      console.log("✅ Header Component:", headerComponent);
      console.log("✅ Body Component:", bodyComponent);
      console.log("✅ Footer Component:", footerComponent);

      if (headerComponent) {
        headerFormat = headerComponent.format?.toUpperCase() || "NONE";
        headerText = headerComponent.text || headerComponent.example || "";
      }
      if (bodyComponent) {
        bodyText = bodyComponent.text || "";
      }
      if (footerComponent) {
        footerText = footerComponent.text || "";
      }
    } else {
      headerFormat = tpl.type?.toUpperCase() || "NONE";
      bodyText = tpl.content || "";
    }

    setHeader({ format: headerFormat, text: headerText });
    setPreview(bodyText);
    setFooter(footerText);
    setMediaUrl("");

    console.log("✅ Parsed Header Format:", headerFormat);
    console.log("✅ Parsed Header Text:", headerText);
    console.log("✅ Parsed Body Text:", bodyText);
    console.log("✅ Parsed Footer Text:", footerText);
  }, [selectedTpl, templates]);

  useEffect(() => {
    if (!submitId) return;
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(pollRef.current);
  }, [submitId]);

  const fetchStatus = async () => {
    try {
      const res = await getSubmissionStatus(submitId);
      setStatuses(res.data);
      console.log("✅ Fetched submission status:", res.data);
      if (
        res.data.every((r) =>
          ["Delivered", "Failed", "Sent"].includes(r.delivery_status)
        )
      ) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err) {
      console.error("❌ Status fetch failed:", err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    setCsvRecipients(lines.length > 1 ? lines.slice(1) : []);
    console.log("✅ CSV lines loaded:", lines);
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const tpl = templates.find((t) => t.id === selectedTpl);
    if (!tpl) {
      toast.error("Invalid template selected for upload");
      return;
    }

    console.log("✅ Uploading media for template:", tpl);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post(
        `/api/templates/${tpl.id}/upload-media`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("✅ Upload API response:", res.data);
      if (!res.data.url) {
        console.error("❌ Upload response missing URL");
        toast.error("Upload failed (no URL returned)");
        return;
      }
      setMediaUrl(res.data.url);
      toast.success("Media uploaded successfully");
      console.log("✅ Media uploaded URL:", res.data.url);
    } catch (err) {
      console.error("❌ Media upload failed:", err);
      toast.error("Failed to upload media");
    }
  };

  const handleSend = async () => {
    const rawLines =
      mode === "text"
        ? textRecipients
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter((l) => l)
        : csvRecipients.map((l) => l.trim()).filter((l) => l);

    if (!selectedTpl || rawLines.length === 0) {
      toast.warning("Please select a template and enter recipients.");
      return;
    }

    if (
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format) &&
      !mediaUrl.trim()
    ) {
      toast.warning(`Please upload the ${header.format} file.`);
      return;
    }

    // --- NEW: enforce format when header has a variable placeholder ---
    const headerHasVar =
      header.format === "TEXT" && /{{\s*\d+\s*}}/.test(header.text || "");
    if (headerHasVar) {
      const malformed = rawLines.filter((line) => {
        const parts = line.split(",").map((p) => p.trim());
        // need at least phone, headerValue, and one body value
        return parts.length < 3 || !parts[1] || !parts[2];
      });
      if (malformed.length > 0) {
        toast.warning(
          `Malformed recipient lines for template with header variable. Expected: phone,headerValue,bodyValue. Examples: ${malformed
            .slice(0, 3)
            .join("; ")}${malformed.length > 3 ? "..." : ""}`
        );
        return;
      }
    }
    // -------------------------------------------------------------------

    const numbers = [];
    const params = [];

    rawLines.forEach((line) => {
      const parts = line.split(",").map((p) => p.trim());
      const phone = parts.shift();
      numbers.push(phone.startsWith("+") ? phone : "+91" + phone);
      params.push(parts);
    });

    console.log("✅ Prepared numbers:", numbers);
    console.log("✅ Prepared params:", params);

    if (
      submitMode === "Schedule" &&
      (!scheduledDateTime || dayjs(scheduledDateTime).isBefore(dayjs()))
    ) {
      toast.warning("Please select a valid future date/time.");
      return;
    }

    // (You can keep this if you ever use components, backend ignores it currently)
    const components = [];
    components.push({
      type: "body",
      parameters: params[0]?.map((v) => ({ type: "text", text: v })) || [],
    });
    if (header.format === "TEXT" && header.text) {
      components.push({
        type: "header",
        parameters: [
          {
            type: "text",
            text: header.text,
          },
        ],
      });
    }
    if (
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format) &&
      mediaUrl.trim()
    ) {
      components.push({
        type: "header",
        parameters: [
          {
            type: header.format.toLowerCase(),
            [header.format.toLowerCase()]: { link: mediaUrl.trim() },
          },
        ],
      });
    }

    const payload = {
      numbers: rawLines, // legacy shape your backend expects
      mode: submitMode,
      scheduleAt: submitMode === "Schedule" ? scheduledDateTime : null,
      mediaUrl: mediaUrl.trim(),
    };

    console.log("✅ Sending payload:", JSON.stringify(payload, null, 2));

    setSending(true);
    try {
      const res = await sendTemplateMessage(selectedTpl, payload);
      console.log("✅ Send response:", res.data);
      if (res.data.submitId) setSubmitId(res.data.submitId);
      toast.success(res.data.message || "Submitted successfully");
      setTextRecipients("");
      setCsvRecipients([]);
      setFileName("");
      setMediaUrl("");
    } catch (err) {
      console.error(
        "❌ WhatsApp API Error:",
        err.response?.data || err.message
      );
      toast.error("Failed to submit message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Container disableGutters sx={{ mt: 0 }}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Paper elevation={3} sx={{ p: 3, width: 500 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: 400,
            }}
          >
            {/* Submit Mode */}
            <FormControl fullWidth size="small">
              <InputLabel>Mode</InputLabel>
              <Select
                value={submitMode}
                label="Mode"
                onChange={(e) => setSubmitMode(e.target.value)}
              >
                <MenuItem value="Immediate">Immediate</MenuItem>
                <MenuItem value="Schedule">Schedule</MenuItem>
              </Select>
            </FormControl>
            {submitMode === "Schedule" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Schedule At"
                  value={dayjs(scheduledDateTime)}
                  onChange={(dt) => setScheduledDateTime(dt.toISOString())}
                  renderInput={(props) => (
                    <TextField fullWidth size="small" {...props} />
                  )}
                />
              </LocalizationProvider>
            )}

            {/* Template Selection */}
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select
                value={selectedTpl}
                label="Template"
                onChange={(e) => setSelectedTpl(e.target.value)}
              >
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Header */}
            {header.format !== "NONE" && (
              <Box>
                {header.format === "TEXT" && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Header Text"
                    value={header.text}
                    InputProps={{
                      readOnly: true,
                      style: { backgroundColor: "#f5f5f5" },
                    }}
                  />
                )}
                {["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format) && (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadFileIcon />}
                      size="small"
                    >
                      {mediaUrl ? "Uploaded" : `Upload ${header.format}`}
                      <input
                        type="file"
                        hidden
                        accept={
                          header.format === "IMAGE"
                            ? "image/*"
                            : header.format === "VIDEO"
                            ? "video/*"
                            : "*/*"
                        }
                        onChange={handleMediaUpload}
                      />
                    </Button>
                    {mediaUrl && (
                      <Typography variant="caption" color="success.main">
                        File uploaded successfully.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Preview */}
            <TextField
              label="Template Preview"
              value={preview}
              multiline
              rows={4}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f5f5f5" },
              }}
            />

            {/* Recipient Mode */}
            <Tabs
              value={mode}
              onChange={(_, v) => setMode(v)}
              variant="fullWidth"
            >
              <Tab label="Text Input" value="text" />
              <Tab label="CSV Upload" value="csv" />
            </Tabs>
            {mode === "text" ? (
              <TextField
                label="Recipients (one per line)"
                placeholder="9876543210,param1,param2,..."
                value={textRecipients}
                onChange={(e) => setTextRecipients(e.target.value)}
                multiline
                rows={4}
                fullWidth
                size="small"
              />
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  size="small"
                >
                  {fileName || "Upload CSV"}
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={handleFileUpload}
                  />
                </Button>
                {csvRecipients.length > 0 && (
                  <Typography variant="caption">
                    {csvRecipients.length} lines loaded
                  </Typography>
                )}
              </Box>
            )}

            {/* Send */}
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={sending}
              onClick={handleSend}
            >
              {sending ? "Sending..." : "Send"}
            </Button>
          </Box>
          {/* (omitted here for space) */}
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: "#ece5dd",
            borderRadius: "8px",
            width: 500,
            minHeight: 400,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Message Preview
          </Typography>
          {header.format === "TEXT" && header.text && (
            <Box
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "#dcf8c6",
                borderRadius: "7px 7px 0px 0px",
                px: 1.5,
                py: 1,
                mb: 0,
                maxWidth: "80%",
                minWidth: "80%",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {header.text}
              </Typography>
            </Box>
          )}
          {["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format) && (
            <Box
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "#dcf8c6",
                borderRadius: "7px 7px 0px 0px",
                p: 1,
                mb: 0,
                maxWidth: "80%",
              }}
            >
              {mediaUrl ? (
                <>
                  {header.format === "IMAGE" ? (
                    <img
                      src={mediaUrl}
                      alt="Header"
                      style={{ maxWidth: "100%", borderRadius: "5px" }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                      {header.format} attached
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "#555" }}
                >
                  {header.format} not uploaded
                </Typography>
              )}
            </Box>
          )}
          {preview && (
            <Box
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "#dcf8c6",
                borderRadius: "0px 0px 7px 7px",
                px: 1.5,
                py: 1,
                mb: footer ? 1 : 0,
                maxWidth: "80%",
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {preview}
              </Typography>
            </Box>
          )}
          {footer && (
            <Typography
              variant="caption"
              sx={{
                alignSelf: "flex-start",
                color: "#555",
                fontStyle: "italic",
                mt: 1,
              }}
            >
              {footer}
            </Typography>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
